/**
 * Toast — replaces window.alert(). `useToast()` returns success/error/info.
 * Mount <ToastProvider> once near the app root.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

type ToastKind = 'success' | 'error' | 'info';
interface ToastItem { id: number; kind: ToastKind; msg: string; }

interface ToastApi {
  success: (msg: string) => void;
  error: (msg: string) => void;
  info: (msg: string) => void;
}

const ToastCtx = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const remove = useCallback((id: number) => {
    setItems((xs) => xs.filter((x) => x.id !== id));
  }, []);

  const push = useCallback((kind: ToastKind, msg: string) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, kind, msg }]);
    setTimeout(() => remove(id), 4000);
  }, [remove]);

  const api = useMemo<ToastApi>(() => ({
    success: (m) => push('success', m),
    error: (m) => push('error', m),
    info: (m) => push('info', m),
  }), [push]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      {/* role/aria-live: screen reader + เครื่องมือตรวจอัตโนมัติหา toast เจอ (tester 22 ก.ย. หาไม่เจอ = false alarm) */}
      <div role="status" aria-live="polite" className="fixed top-4 inset-x-0 z-[60] flex flex-col items-center gap-2 px-4 pointer-events-none">
        {items.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto w-full max-w-sm rounded-lg shadow-lg border bg-white px-3 py-2.5 text-sm flex items-start gap-2 ${
              t.kind === 'success' ? 'border-green-200' : t.kind === 'error' ? 'border-red-200' : 'border-gray-200'
            }`}
          >
            {t.kind === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
            ) : t.kind === 'error' ? (
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            ) : (
              <Info className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
            )}
            <span className="flex-1 text-gray-800">{t.msg}</span>
            <button onClick={() => remove(t.id)} aria-label="ปิด">
              <X className="w-3.5 h-3.5 text-gray-400" />
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
